import React, { useState, useEffect } from 'react';
import { generateMoveQuestion, shouldShowQuestion } from '../../utils/moveQuestions';
import { getLineStatus } from '../../utils/srs';
import Icon from './Icon';
import { LogoMark } from './Logo';

const STATUS_ICONS = { new: '', due: '↻ ', learning: '✓ ', perfected: '★ ' };

const STATUS_BADGES = {
  new: { label: 'New', className: 'bg-ink-750 text-ink-300' },
  due: { label: 'To review', className: 'bg-amber-500/15 text-amber-300' },
  learning: { label: 'Learned', className: 'bg-brand-500/15 text-brand-300' },
  perfected: { label: 'Perfected', className: 'bg-brand-400/20 text-brand-200' }
};

const getFeedbackTone = (feedback) => {
  if (/wrong|error|cannot/i.test(feedback)) return 'border-rose-500/40 bg-rose-500/10 text-rose-100';
  if (/perfect|learned|correct|completed/i.test(feedback)) return 'border-brand-500/40 bg-brand-500/10 text-brand-100';
  if (/nothing to review|learn a line first|discovered every/i.test(feedback)) return 'border-amber-500/40 bg-amber-500/10 text-amber-100';
  return 'border-ink-600 bg-ink-800 text-ink-200';
};

// A message from the coach, displayed as a chat bubble
const Bubble = ({ children, tone = 'light' }) => (
  <div className={`rounded-2xl rounded-tl-md px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
    tone === 'light' ? 'bg-ink-100 text-ink-900' : `border ${tone}`
  }`}>
    {children}
  </div>
);

// Comments of the current position: the last move played and the reply that led here
const getCurrentComment = (line, moveIndex) => {
  const comments = line?.comments;
  if (!comments) return '';
  return [comments[moveIndex - 1], comments[moveIndex]].filter(Boolean).join('\n\n');
};

/**
 * Game Sidebar Component
 * Single Responsibility: Rendering game information and line selection
 */
const GameSidebar = ({ 
  opening, 
  currentLine, 
  currentLineId,
  moveIndex,
  isPlayerTurn,
  game,
  selectedSquare, 
  possibleMoves, 
  feedback,
  onLineChange,
  mode = 'learn',
  onModeChange,
  progress,
  cards
}) => {
  const [showQuestion, setShowQuestion] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  
  // Generate new question/hint when move changes
  useEffect(() => {
    if (currentLine && moveIndex < currentLine.moves.length && isPlayerTurn) {
      // Always show direct move indication for the first move
      const shouldQuestion = moveIndex === 0 ? false : shouldShowQuestion(0.7); // 70% chance of question
      setShowQuestion(shouldQuestion);
      
      if (shouldQuestion && game) {
        const question = generateMoveQuestion(currentLine.moves[moveIndex], game);
        setCurrentQuestion(question);
      }
    }
  }, [moveIndex, currentLine, isPlayerTurn, game]);
  const isMoveToPlay = currentLine && moveIndex < currentLine.moves.length && isPlayerTurn;
  const comment = mode === 'learn' ? getCurrentComment(currentLine, moveIndex) : '';
  const status = currentLine ? getLineStatus(currentLine.id, cards) : 'new';
  const lines = opening?.lines || [];
  const mainLines = lines.filter(line => line.trap !== 1 && line.trap !== true);
  const trapLines = lines.filter(line => line.trap === 1 || line.trap === true);

  const modeButton = (key, icon, label, detail, activeClass) => (
    <button
      onClick={() => onModeChange(key)}
      className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${
        mode === key ? activeClass : 'border-ink-700 bg-ink-900/40 hover:border-ink-500'
      }`}
      aria-pressed={mode === key}
    >
      <div className="flex items-center gap-2 text-sm font-bold text-ink-100">
        <Icon name={icon} size={16} /> {label}
      </div>
      <div className="text-xs text-ink-400 mt-0.5">{detail}</div>
    </button>
  );

  return (
    <aside className="card w-full lg:w-[360px] flex-shrink-0 flex flex-col p-4 gap-4 lg:max-h-full lg:overflow-y-auto">
      {/* Modes */}
      {progress && onModeChange && (
        <div className="grid grid-cols-2 gap-2">
          {modeButton('learn', 'book', 'Learn', `${progress.learned}/${progress.total} discovered`, 'border-brand-500/60 bg-brand-500/10')}
          {modeButton('practice', 'target', 'Practice', (
            <><span className={progress.due > 0 ? 'text-amber-300 font-semibold' : ''}>{progress.due} due</span> · {progress.perfected} perfected</>
          ), 'border-amber-500/60 bg-amber-500/10')}
        </div>
      )}

      {/* Variation */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="variation-select" className="label mb-0">Variation</label>
          <span className={`badge ${STATUS_BADGES[status].className}`}>{STATUS_BADGES[status].label}</span>
        </div>
        <div className="relative">
          <select
            id="variation-select"
            value={currentLineId || currentLine?.id || ''}
            onChange={onLineChange}
            className="input appearance-none pr-9 font-semibold"
          >
            {mainLines.length > 0 && (
              <optgroup label="Main lines">
                {mainLines.map(line => (
                  <option key={line.id} value={line.id}>
                    {STATUS_ICONS[getLineStatus(line.id, cards)] || ''}{line.name}
                  </option>
                ))}
              </optgroup>
            )}
            {trapLines.length > 0 && (
              <optgroup label="Traps">
                {trapLines.map(line => (
                  <option key={line.id} value={line.id}>
                    {STATUS_ICONS[getLineStatus(line.id, cards)] || ''}{line.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <Icon name="chevronDown" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
        </div>
        {currentLine?.description && (
          <p className="hidden lg:block text-xs text-ink-400 mt-2">{currentLine.description}</p>
        )}
      </div>

      {/* Coach */}
      <div className="flex gap-2.5 lg:flex-1 min-h-[3rem]">
        <LogoMark size={30} className="mt-0.5" />
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {comment && <Bubble>{comment}</Bubble>}

          {isMoveToPlay && mode === 'practice' && (
            <Bubble>Your move. Play the line from memory.</Bubble>
          )}
          {isMoveToPlay && mode === 'learn' && (
            showQuestion && currentQuestion
              ? <Bubble>{currentQuestion.text}</Bubble>
              : <Bubble>Play <strong className="font-extrabold">{currentLine.moves[moveIndex]}</strong></Bubble>
          )}
          {!isMoveToPlay && !feedback && currentLine && moveIndex < currentLine.moves.length && (
            <div className="text-xs text-ink-400 italic px-1 py-2">Your opponent is thinking…</div>
          )}

          {feedback && feedback.trim() !== '' && (
            <Bubble tone={getFeedbackTone(feedback)}>{feedback}</Bubble>
          )}
        </div>
      </div>
    </aside>
  );
};

export default GameSidebar;
